import {
	type AppRecDismissalData,
	type GeneralError,
	type MutationResponse,
	type MutationResponseData,
	type MutationResult,
} from '../types';

/**
 * The mutation result holds the necessary data that extract from the fetch response.
 */
export class GraphQlMutationResult<T extends AppRecDismissalData | undefined>
	implements MutationResult<T>
{
	constructor(
		public code: number,
		public reason: string,
		public response?: {
			data?: MutationResponseData<T>;
			errors?: string[];
		},
	) {}
}

abstract class MutationResultCreator<T extends AppRecDismissalData | undefined> {
	abstract makeMutationResult(): MutationResult<T>;
}

/**
 * This class is responsible for creating a mutation result for a non-OK fetch response.
 */
export class NonOkMutationResultCreator extends MutationResultCreator<undefined> {
	constructor(private fetchResponse: Response) {
		super();
	}

	makeMutationResult(): MutationResult<undefined> {
		return new GraphQlMutationResult<undefined>(
			this.fetchResponse.status,
			this.fetchResponse.statusText,
		);
	}
}

/**
 * This class is responsible for creating a mutation result for a fetch response that contains general errors.
 */
export class GeneralMutationErrorResultCreator extends MutationResultCreator<undefined> {
	constructor(private errors: GeneralError[]) {
		super();
	}

	makeMutationResult(): MutationResult<undefined> {
		const [{ extensions }] = this.errors;
		const [{ statusCode, errorType }] = Array.isArray(extensions) ? extensions : [extensions];

		return new GraphQlMutationResult<undefined>(statusCode, errorType, {
			errors: this.errors.map((error) => error.message),
		});
	}
}

/**
 * This class is responsible for creating a mutation result for a fetch response that missing the key attributes.
 */
export class MissingMutationDataResultCreator extends MutationResultCreator<undefined> {
	constructor(private fetchResponse: Response) {
		super();
	}

	makeMutationResult(): MutationResult<undefined> {
		return new GraphQlMutationResult<undefined>(
			this.fetchResponse.status,
			this.fetchResponse.statusText,
			{
				errors: ['Received data has missing attributes'],
			},
		);
	}
}

/**
 * This class is responsible for creating a mutation result for a fetch response that contains mutation errors.
 */
export class MutationErrorResultCreator<
	T extends AppRecDismissalData,
> extends MutationResultCreator<T> {
	constructor(private mutationResponse: MutationResponse) {
		super();
	}

	makeMutationResult(): MutationResult<T> {
		const { errors } = this.mutationResponse;

		// This is meant to be a runtime error during development.
		if (!Array.isArray(errors) || !errors.length) {
			// The error is not expected to happen inside this function.
			throw new Error('Invalid mutation response: missing errors');
		}

		// This is a safe assertion because we already checked that the response contains errors.
		const [
			{
				extensions: { statusCode, errorType },
			},
		] = errors;

		return new GraphQlMutationResult<T>(statusCode, errorType, {
			errors: errors.map((error) => error.message),
		});
	}
}

export abstract class MutationSuccessResultCreator<
	T extends AppRecDismissalData,
> extends MutationResultCreator<T> {
	constructor(
		protected response: Response,
		protected mutationResponse: T,
	) {
		super();
	}
}
